import React, { useState, useEffect } from 'react';
import BidBuilder from './components/BidBuilder';
import Login from './components/Login';
import Landing from './components/Landing';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import SignupWizard from './components/SignupWizard';
import { DocView, NewClientModal, InvoiceModal } from './components/extraComponents';
import { Overlay, MHdr, Field } from './components/shared';
import Clients from './components/Clients';
import Invoices from './components/Invoices';
import ScanInvoice from './components/ScanInvoice';
import SupplyHousePage from './components/SupplyHousePage';
import AiAccuracy from './components/AiAccuracy';
import BetaRequests from './components/BetaRequests';
import AdminCompanies from './components/AdminCompanies';
import CustomBilling from './components/CustomBilling';
import Settings from './components/Settings';
import Dashboard from './components/Dashboard';
import Recurring from './components/Recurring';
import Expenses from './components/Expenses';
import Receipts from './components/Receipts';
import Jobs from './components/Jobs';
import Payments from './components/Payments';
import Deposits from './components/Deposits';
import GlobalSearch from './components/GlobalSearch';
import Messages from './components/Messages';
import Calendar from './components/Calendar';
import Creditors from './components/Creditors';
import Payroll from './components/Payroll';
import InvitePage from './components/InvitePage';
import EmployeeApp from './components/EmployeeApp';
import Reports from './components/Reports';
import Permits from './components/Permits';
import ToolFrame from './components/ToolFrame';
import Inventory from './components/Inventory';
import AVLightingHub from './components/AVLighting/AVLightingHub';
import { D, st, toArr, fmt, fmtDate, calcTotal, getToken, apiGet, apiPost, apiPut, apiDel, setTaxRate } from './constants';
import { BID_TEMPLATES, MAT_CATALOG, MAT_CATALOG_FLAT } from './data';

// Expose globals that BidBuilder references directly
window.BID_TEMPLATES = BID_TEMPLATES;
window.MAT_CATALOG = MAT_CATALOG;
window.MAT_CATALOG_FLAT = MAT_CATALOG_FLAT;

export default function App() {
  const inviteToken = window.location.pathname.match(/^\/invite\/([a-f0-9]{40})$/)?.[1];
  const empSession = (() => { try { return JSON.parse(localStorage.getItem('eb:employee')); } catch(_) { return null; } })();
  const [authView, setAuthView] = useState(() => { const p = new URLSearchParams(window.location.search); return p.get('signup') === '1' ? 'signup' : 'landing'; });
  const [navigateTo, setNavigateTo] = useState(null);
  const [bids, setBids] = useState([]);
  const [clients, setClients] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [matPrices, setMatPrices] = useState({});
  const [matPriceHistory, setMatPriceHistory] = useState({});
  // Flat-rate service price book. Lifted out of InvoiceModal so the modal does
  // not have to refetch all of /data/settings every time it opens.
  const [services, setServices] = useState({});
  const [markup, setMarkup] = useState(null);
  const [permits, setPermits] = useState([]);
  const [creditors, setCreditorsState] = useState({vendors:[], receipts:[]});
  const setCreditors = (updater) => {
    setCreditorsState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      apiPut("/data/settings", { creditors: next }).catch(()=>{});
      return next;
    });
  };
  const [companyInfo, setCompanyInfo] = useState({name:"AmpBooks",address:"Florida",licenseNum:""});
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = navOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [navOpen]);
  const [clientModal, setClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [onClientCreated, setOnClientCreated] = useState(null);
  const openClientModal = (client, onCreated) => { setEditingClient(client || null); setOnClientCreated(() => onCreated || null); setClientModal(true); };
  const [page, setPage] = useState("dashboard");
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [error, setError] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    Promise.all([
      apiGet("/data/bids"),
      apiGet("/data/clients"),
      apiGet("/data/estimates"),
      apiGet("/data/invoices"),
      apiGet("/data/jobs"),
      apiGet("/data/settings"),
      apiGet("/auth/me"),
      apiGet("/data/company"),
      apiGet("/data/payments"),
      apiGet("/data/deposits"),
      apiGet("/data/recurring"),
      apiGet("/data/expenses"),
      apiGet("/data/permits"),
      apiGet("/billing/status").catch(()=>null),
    ]).then(([b, c, e, i, j, s, m, comp, pmts, deps, rec, exp, perms, billing]) => {
      setMe(m);
      if (billing) setBillingStatus(billing);
      setBids(toArr(b).map(x=>({...x,clientId:x.client_id||x.clientId,laborHours:x.labor_hours||{},laborRates:x.labor_rates||{},permitFee:x.permit_fee,overheadPct:x.overhead_pct,profitPct:x.profit_pct,estimateId:x.estimate_id})));
      setClients(toArr(c));
      setEstimates(toArr(e).map(x=>({...x,clientId:x.client_id||x.clientId,depositRequired:!!x.deposit_required,depositAmount:x.deposit_amount||"",depositType:x.deposit_type||"percent"})));
      setInvoices(toArr(i).map(x=>({...x,clientId:x.client_id||x.clientId})));
      setJobs(toArr(j).map(x=>({...x,clientId:x.client_id||x.clientId,estimateId:x.estimate_id||x.estimateId,endDate:x.end_date||x.endDate,fieldNotes:x.fieldNotes||x.field_notes||[]})));
      if (s?.markup) setMarkup(s.markup);
      if (s?.matPrices) setMatPrices(s.matPrices);
      if (s?.matPriceHistory) setMatPriceHistory(s.matPriceHistory);
      if (s?.services) setServices(s.services);
      setPayments(toArr(pmts));
      setDeposits(toArr(deps));
      setRecurring(toArr(rec).map(x=>({...x,dueDay:x.due_day})));
      setExpenses(toArr(exp).map(x=>({...x,clientId:x.client_id||x.clientId,jobId:x.linked_job_id||null})));
      setPermits(toArr(perms).map(x=>({
        ...x,
        clientId: x.clientId ?? x.client_id,
        jobId: x.jobId ?? x.job_id,
        estimateId: x.estimateId ?? x.estimate_id,
        contractorLic: x.contractorLic ?? x.contractor_lic,
        feesPaid: !!(x.feesPaid ?? x.fees_paid),
        appliedDate: x.appliedDate ?? x.applied_date,
        approvedDate: x.approvedDate ?? x.approved_date,
        expiresDate: x.expiresDate ?? x.expires_date,
        closedDate: x.closedDate ?? x.closed_date,
        inspections: x.inspections || [],
        attachments: x.attachments || [],
      })));
      if (comp) {
        try { const mk = typeof s?.markup === 'string' ? JSON.parse(s.markup) : (s?.markup || {}); setTaxRate(mk.taxRate); } catch(_) { setTaxRate(0); }
        // The API returns camelCase and already-parsed objects. This read
        // s.payment_methods (snake_case) inside a try/catch, so payment methods
        // silently never reached companyInfo - and companyInfo is what the
        // invoice send modal renders from.
        const pm = s?.paymentMethods || {};
        setCompanyInfo({id:comp.id,name:comp.name||'',phone:comp.phone||'',address:comp.address||'',licenseNum:comp.license_num||'',email:comp.email||'',billingEmail:comp.billing_email||'',paymentMethods:pm,paymentTerms:s?.paymentTerms||'',scopeNote:s?.scopeNote||'',lateFeeEnabled:!!s?.lateFeeEnabled,features:comp.features||{}});
      }
    }).catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onFocus = () => {
      if (!getToken()) return;
      apiGet("/data/estimates").then(e=>setEstimates(toArr(e).map(x=>({...x,clientId:x.client_id||x.clientId,depositRequired:!!x.deposit_required,depositAmount:x.deposit_amount||"",depositType:x.deposit_type||"percent"})))).catch(()=>{});
      apiGet("/data/invoices").then(i=>setInvoices(toArr(i).map(x=>({...x,clientId:x.client_id||x.clientId})))).catch(()=>{});
      apiGet("/data/jobs").then(j=>setJobs(toArr(j).map(x=>({...x,clientId:x.client_id||x.clientId,estimateId:x.estimate_id||x.estimateId,endDate:x.end_date||x.endDate,fieldNotes:x.fieldNotes||x.field_notes||[]})))).catch(()=>{});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (inviteToken) return <InvitePage token={inviteToken} />;
  if (!getToken() && empSession?.userId) return <EmployeeApp identity={empSession} />;
  if (loading) return <div style={{...st.app,display:"flex",alignItems:"center",justifyContent:"center",height:"100vh"}}><div style={{color:D.muted}}>Loading AmpBooks V2…</div></div>;
  if (!getToken() || error) {
    if (authView === 'signup') return <SignupWizard onComplete={() => window.location.reload()} onBack={() => setAuthView('landing')} onLogin={() => setAuthView('login')}/>;
    if (authView === 'login') return <Login onLogin={() => window.location.reload()} />;
    if (authView === 'terms') return <Terms onBack={() => setAuthView('landing')}/>;
    if (authView === 'privacy') return <Privacy onBack={() => setAuthView('landing')}/>;
    return <Landing onStartTrial={() => setAuthView('signup')} onLogin={() => setAuthView('login')} onTerms={() => setAuthView('terms')} onPrivacy={() => setAuthView('privacy')}/>;
  }

  const clientName = id => toArr(clients).find(c=>c.id===id)?.name || "Unknown";
  const openInvoiceModal = (inv=null, fromEst=null, extraItem=null, presetClientId=null, presetJobId=null) => {
    const extraItems = extraItem == null ? [] : (Array.isArray(extraItem) ? extraItem : [extraItem]);
    if (inv) {
      const items = toArr(inv.items).map(i=>({...i}));
      items.push(...extraItems.map(i=>({...i})));
      setInvoiceModal({...inv, items});
    } else {
      setInvoiceModal({
        id: null,
        clientId: presetClientId ?? fromEst?.clientId ?? clients[0]?.id ?? 1,
        estimateId: fromEst?.id ?? null,
        jobId: presetJobId ?? null,
        number: `INV-${String(Math.max(0,...toArr(invoices).map(i=>parseInt(i.number?.replace("INV-","")||0)))+1).padStart(3,"0")}`,
        date: new Date().toISOString().slice(0,10),
        dueDate: new Date(Date.now()+30*86400000).toISOString().slice(0,10),
        status: "Draft",
        notes: "",
        paidDate: null,
        items: extraItems.length ? extraItems.map(i=>({...i})) : (fromEst?.items?.length ? fromEst.items.map(i=>({...i})) : [{desc:'',qty:1,unit:'hr',rate:0}]),
      });
    }
  };

  const apiSaveBid = async (bid) => {
    if (bid.id) {
      await apiPut("/data/bids/"+bid.id, bid);
      setBids(prev => toArr(prev).map(b => b.id===bid.id ? {...b,...bid} : b));
    } else {
      const created = await apiPost("/data/bids", bid);
      setBids(prev => [...toArr(prev), {...created,clientId:created.client_id||bid.clientId,laborHours:created.labor_hours||{},laborRates:created.labor_rates||{},permitFee:created.permit_fee,overheadPct:created.overhead_pct,profitPct:created.profit_pct,estimateId:created.estimate_id}]);
    }
  };

  const apiDeleteBid = async (id) => {
    await apiDel("/data/bids/"+id);
    setBids(prev => toArr(prev).filter(b => b.id !== id));
  };

  const apiSavePayment = async (pmt) => {
    const created = await apiPost("/data/payments", {invoiceId:pmt.invoice_id, clientId:pmt.client_id, amount:pmt.amount, date:pmt.date, method:pmt.method, notes:pmt.notes});
    setPayments(prev => [...toArr(prev), {...pmt, id:created.id}]);
  };

  const apiDeletePayment = async (id) => {
    await apiDel("/data/payments/"+id);
    setPayments(prev => toArr(prev).filter(p => p.id !== id));
  };

  const apiSaveDeposit = async (dep) => {
    const created = await apiPost("/data/deposits", {invoiceId:dep.invoice_id||null, clientId:dep.client_id||null, estimateId:dep.estimate_id||null, amount:dep.amount, date:dep.date, method:dep.method, notes:dep.notes});
    setDeposits(prev => [...toArr(prev), {...dep, id:created.id}]);
  };

  const apiDeleteDeposit = async (id) => {
    await apiDel("/data/deposits/"+id);
    setDeposits(prev => toArr(prev).filter(d => d.id !== id));
  };

  const apiSaveRecurring = async (rec) => {
    if (rec.id) {
      await apiPut("/data/recurring/"+rec.id, {description:rec.description, amount:rec.amount, frequency:rec.frequency, dueDay:rec.dueDay, category:rec.category, vendor:rec.vendor, active:rec.active});
      setRecurring(prev => toArr(prev).map(r => r.id===rec.id ? {...r, ...rec} : r));
    } else {
      const created = await apiPost("/data/recurring", {description:rec.description, amount:rec.amount, frequency:rec.frequency, dueDay:rec.dueDay, category:rec.category, vendor:rec.vendor});
      setRecurring(prev => [...toArr(prev), {...rec, id:created.id, active:1}]);
    }
  };

  const apiDeleteRecurring = async (id) => {
    await apiDel("/data/recurring/"+id);
    setRecurring(prev => toArr(prev).filter(r => r.id !== id));
  };

  const apiToggleRecurring = async (rec) => {
    const active = rec.active ? 0 : 1;
    await apiPut("/data/recurring/"+rec.id, {active});
    setRecurring(prev => toArr(prev).map(r => r.id===rec.id ? {...r, active} : r));
  };

  const apiSaveJob = async (job) => {
    if (job.id) {
      await apiPut("/data/jobs/"+job.id, {clientId:job.clientId||null, estimateId:job.estimateId||null, bidId:job.bidId||null, title:job.title, status:job.status, date:job.date, time:job.time, endDate:job.endDate, address:job.address, description:job.description});
      setJobs(prev => toArr(prev).map(j => j.id===job.id ? {...j, ...job} : j));
    } else {
      const created = await apiPost("/data/jobs", {clientId:job.clientId||null, estimateId:job.estimateId||null, bidId:job.bidId||null, title:job.title, status:job.status, date:job.date, time:job.time, endDate:job.endDate, address:job.address, description:job.description});
      setJobs(prev => [...toArr(prev), {...job, id:created.id}]);
    }
  };
  const apiDeleteJob = async (id) => { await apiDel("/data/jobs/"+id); setJobs(prev => toArr(prev).filter(j => j.id !== id)); };

  const apiSaveMatPrices = async (newMatPrices, newMatPriceHistory) => {
    await apiPut("/data/mat-prices", { matPrices: newMatPrices, matPriceHistory: newMatPriceHistory });
    setMatPrices(newMatPrices);
    setMatPriceHistory(newMatPriceHistory);
  };

  const apiSavePermit = async (permit) => {
    if (permit.id) {
      await apiPut("/data/permits/"+permit.id, permit);
      setPermits(prev => toArr(prev).map(p => p.id===permit.id ? {...p, ...permit} : p));
      return permit;
    } else {
      const created = await apiPost("/data/permits", permit);
      const withId = {...permit, id:created.id};
      setPermits(prev => [...toArr(prev), withId]);
      return withId;
    }
  };
  const apiDeletePermit = async (id) => { await apiDel("/data/permits/"+id); setPermits(prev => toArr(prev).filter(p => p.id !== id)); };

  const apiSaveJobNotes = async (jobId, fieldNotes) => {
    await apiPut("/data/jobs/"+jobId, { fieldNotes });
    setJobs(prev => toArr(prev).map(j => j.id===jobId ? {...j, fieldNotes} : j));
  };

  const submitFeedback = async () => {
    if (!feedbackText.trim()) return;
    setFeedbackSending(true);
    try {
      await apiPost("/data/feedback", {message:feedbackText.trim(), page});
      setFeedbackText("");
      setFeedbackSent(true);
      setTimeout(()=>{ setFeedbackModal(false); setFeedbackSent(false); }, 1500);
    } catch(e) { alert("Failed to send feedback: "+e.message); }
    setFeedbackSending(false);
  };

  const apiSaveExpense = async (exp) => {
    if (exp.id) {
      await apiPut("/data/expenses/"+exp.id, {date:exp.date, category:exp.category, description:exp.description, amount:exp.amount, vendor:exp.vendor, billable:exp.billable, notes:exp.notes, linkedJobId:exp.jobId||null});
      setExpenses(prev => toArr(prev).map(e => e.id===exp.id ? {...e, ...exp} : e));
    } else {
      const created = await apiPost("/data/expenses", {clientId:exp.clientId, date:exp.date, category:exp.category, description:exp.description, amount:exp.amount, vendor:exp.vendor, billable:exp.billable, notes:exp.notes, linkedJobId:exp.jobId||null});
      setExpenses(prev => [...toArr(prev), {...exp, id:created.id}]);
    }
  };

  const apiDeleteExpense = async (id) => {
    await apiDel("/data/expenses/"+id);
    setExpenses(prev => toArr(prev).filter(e => e.id !== id));
  };

  const apiUpdateBid = async (id, updates) => {
    await apiPut("/data/bids/"+id, updates);
    setBids(prev => toArr(prev).map(b => b.id===id ? {...b,...updates} : b));
  };

  const apiSaveEstimate = async (est) => {
    if (est.id) {
      const u = await apiPut("/data/estimates/"+est.id, est);
      const updated = {...est,...u,clientId:u.client_id||est.clientId};
      setEstimates(prev => toArr(prev).map(e => e.id===est.id ? updated : e));
      return updated;
    } else {
      const c = await apiPost("/data/estimates", est);
      const created = {...c,clientId:c.client_id||est.clientId};
      setEstimates(prev => [...toArr(prev), created]);
      return created;
    }
  };

  const apiUpdateEstimate = async (id, updates) => {
    await apiPut("/data/estimates/"+id, updates);
    setEstimates(prev => toArr(prev).map(e => e.id===id ? {...e,...updates} : e));
  };

  const apiSaveClient = async (client) => {
    const created = await apiPost("/data/clients", client);
    setClients(prev => [...toArr(prev), created]);
    return created;
  };

  const updateAiBidUsage = (usage) => {
    setMe(prev => prev ? {...prev, company: {...prev.company, ...usage}} : prev);
  };

  const apiUpdateClient = async (id, updates) => {
    await apiPut("/data/clients/"+id, updates);
    setClients(prev => toArr(prev).map(c => c.id===id ? {...c,...updates} : c));
  };

  const apiDeleteClient = async (id) => {
    await apiDel("/data/clients/"+id);
    setClients(prev => toArr(prev).filter(c => c.id !== id));
  };

  const apiSaveInvoice = async (inv) => {
    const created = await apiPost("/data/invoices", inv);
    setInvoices(prev => [...toArr(prev), {...created, clientId: created.client_id||inv.clientId}]);
    return created;
  };

  const apiUpdateInvoice = async (id, updates) => {
    await apiPut("/data/invoices/"+id, updates);
    setInvoices(prev => toArr(prev).map(i => i.id===id ? {...i,...updates} : i));
  };

  const apiDeleteInvoice = async (id) => {
    await apiDel("/data/invoices/"+id);
    setInvoices(prev => toArr(prev).filter(i => i.id !== id));
  };


  const today = new Date().toISOString().slice(0,10);
  const overdueCount = toArr(invoices).filter(i=>i.status!=="Paid"&&i.dueDate&&i.dueDate<today).length;

  const NAV = [
    ...(companyInfo?.features?.led_tools ? [{id:"av-lighting", label:"⚡ AV & Lighting"}] : []),
    {id:"dashboard", label:"Dashboard"},
    {id:"clients", label:"Clients"},
    {id:"bids", label:"Bids"},
    {id:"jobs", label:"Jobs"},
    {id:"calendar", label:"Calendar"},
    {id:"invoices", label:"Invoices"},
    {id:"payments", label:"Payments"},
    {id:"deposits", label:"Deposits"},
    {id:"expenses", label:"Expenses"},
    {id:"receipts", label:"Receipts"},
    {id:"recurring", label:"Recurring"},
    {id:"payroll", label:"Payroll"},
    {id:"inventory", label:"Truck Stock"},
    {id:"creditors", label:"Creditors"},
    {id:"messages", label:"Messages"},
    {id:"scan-invoice", label:"Scan Receipt"},
    {id:"reports", label:"Reports"},
    {id:"settings", label:"Settings"},
    {id:"permits", label:"Permits"},
    {id:"formulas", label:"⚡ Formulas"},
    {id:"fieldtools", label:"⚡ Field Tools"},
    {id:"nec", label:"⚡ NEC Lookup"},
    {id:"riser", label:"⚡ Riser Diagram"},
    {id:"panel-schedule", label:"⚡ Panel Schedule"},
    {id:"supply", label:"🔌 Supply Locator"},
    {id:"supply-pricing", label:"🔌 Supply Prices"},
  ].filter(n => !['helper','apprentice'].includes(me?.user?.role) || !["invoices","payments","deposits","clients","bids","recurring","scan-invoice"].includes(n.id));

  const trialExpired = billingStatus?.plan === 'trial' && billingStatus?.daysRemaining === 0;
  const doCheckout = async (plan) => {
    try {
      const data = await apiPost('/billing/checkout', { plan });
      if (data?.url) window.location.href = data.url;
    } catch(e) { alert('Checkout error: ' + e.message); }
  };

  return (
    <div style={{background:D.bg,minHeight:"100vh",color:D.text,fontFamily:"sans-serif",fontSize:14,display:"flex"}}>
      <div className="ab-hamburger" onClick={()=>setNavOpen(true)} style={{position:"fixed",top:14,left:14,zIndex:70,width:36,height:36,borderRadius:8,background:D.card,border:`1px solid ${D.border}`,alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:18}}>☰</div>
      <div className={"ab-overlay"+(navOpen?" ab-open":"")} onClick={()=>setNavOpen(false)}></div>
      <div className={"ab-sidebar"+(navOpen?" ab-open":"")} style={{width:200,flexShrink:0,borderRight:`1px solid ${D.border}`,paddingTop:20,background:D.bg}}>
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"0 20px",marginBottom:20}}>
          <span style={{fontSize:20}}>⚡</span>
          <span style={{fontSize:16,fontWeight:600,color:D.accent}}>AmpBooks V2</span>
        </div>
        {NAV.map(n=>
          <div key={n.id} style={st.navItem(page===n.id)} onClick={()=>{setPage(n.id);setNavOpen(false);}}>
            {n.label}
            {n.id==="invoices" && overdueCount>0 && <span style={{marginLeft:8,background:D.danger,color:"#fff",borderRadius:10,fontSize:10,fontWeight:600,padding:"1px 7px"}}>{overdueCount}</span>}
          </div>
        )}
        <div style={{padding:"0 12px",marginTop:16,borderTop:"1px solid "+D.border,paddingTop:12}}>
          <div style={{...st.navItem(false),color:D.muted,fontSize:12}} onClick={()=>{localStorage.removeItem('eb:token');window.location.reload();}}>Sign Out</div>
        </div>
      </div>
      <div className="ab-main" style={{flex:1,padding:"24px 28px",minWidth:0}}>
        <div style={{marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
          {me?.user?.isPlatformAdmin && <><button style={{...st.btn(),marginLeft:"auto",fontSize:12}} onClick={()=>setPage("ai-accuracy")}>📊 Analytics</button><button style={{...st.btn(),marginLeft:8,fontSize:12}} onClick={()=>setPage("beta-requests")}>📋 Beta Requests</button><button style={{...st.btn(),marginLeft:8,fontSize:12}} onClick={()=>setPage("admin-companies")}>🏢 Companies</button><button style={{...st.btn(),marginLeft:8,fontSize:12}} onClick={()=>setPage("custom-billing")}>💼 Custom Billing</button></>}
          <button style={{...st.btn(),fontSize:12}} onClick={()=>setFeedbackModal(true)}>Feedback</button>
          <span style={{fontSize:11,background:D.warning+"22",color:D.warning,padding:"2px 8px",borderRadius:20,fontWeight:600}}>BETA</span>
        </div>
        <GlobalSearch
          clients={clients} invoices={invoices} bids={bids} jobs={jobs}
          clientName={clientName} fmt={fmt} fmtDate={fmtDate}
          onNavigate={(type,item)=>setPage(type==="invoice"?"invoices":type==="bid"?"bids":type==="job"?"jobs":type==="client"?"clients":"dashboard")}
        />
        {page==="dashboard" && <Dashboard
          bids={bids} estimates={estimates} invoices={invoices} clients={clients}
          payments={payments} deposits={deposits}
          fmt={fmt} fmtDate={fmtDate} clientName={clientName}
          setPage={setPage} openInvoiceModal={openInvoiceModal} setNavigateTo={setNavigateTo}
        />}
        {page==="expenses" && <Expenses
          expenses={expenses} clients={clients} clientName={clientName} jobs={jobs}
          apiSaveExpense={apiSaveExpense} apiDeleteExpense={apiDeleteExpense}
          fmt={fmt} fmtDate={fmtDate} me={me}
        />}
        {/* Receipts reads its list from GET /data/receipts and its LINES from
            the expenses already in state - an expense row IS a receipt line,
            so the job filter and the detail view cost no extra fetch. */}
        {page==="receipts" && <Receipts
          expenses={expenses} jobs={jobs} invoices={invoices} clientName={clientName} fmt={fmt}
        />}
        {page==="jobs" && <Jobs
          jobs={jobs} clients={clients} estimates={estimates} bids={bids} expenses={expenses} invoices={invoices} markup={markup} openInvoiceModal={openInvoiceModal} clientName={clientName}
          apiSaveJob={apiSaveJob} apiDeleteJob={apiDeleteJob} apiSaveJobNotes={apiSaveJobNotes}
          fmt={fmt} fmtDate={fmtDate} me={me} openClientModal={openClientModal}
        />}
        {page==="calendar" && <Calendar
          jobs={jobs} clients={clients} estimates={estimates} recurring={recurring}
          clientName={clientName} apiSaveJob={apiSaveJob} apiDeleteJob={apiDeleteJob}
        />}
        {page==="recurring" && <Recurring
          recurring={recurring} apiSaveRecurring={apiSaveRecurring} apiDeleteRecurring={apiDeleteRecurring} apiToggleRecurring={apiToggleRecurring}
          fmt={fmt} me={me}
        />}
        {page==="creditors" && <Creditors creditors={creditors} setCreditors={setCreditors} fmt={fmt} fmtDate={fmtDate}/>}
        {page==="payroll" && <Payroll jobs={jobs} fmt={fmt} fmtDate={fmtDate} setPage={setPage}/>}
        {page==="inventory" && <Inventory me={me} jobs={jobs} matPrices={matPrices}/>}
        {page==="reports" && <Reports
          invoices={invoices} clientName={clientName} calcTotal={calcTotal} fmt={fmt} fmtDate={fmtDate}
          expenses={expenses} deposits={deposits} recurring={recurring} companyInfo={companyInfo} me={me}
        />}
        {page==="permits" && <Permits
          permits={permits} clients={clients} jobs={jobs} estimates={estimates} clientName={clientName}
          fmt={fmt} fmtDate={fmtDate} apiSavePermit={apiSavePermit} apiDeletePermit={apiDeletePermit}
        />}
        {page==="formulas" && <ToolFrame src="/electrical-formulas.html" title="Electrical Formulas" onClose={()=>setPage("dashboard")}/>}
        {page==="fieldtools" && <ToolFrame src="/field-tools.html" title="Field Tools" onClose={()=>setPage("dashboard")}/>}
        {page==="nec" && <ToolFrame src="/nec-lookup.html" title="NEC 2023 Code Lookup" onClose={()=>setPage("dashboard")}/>}
        {page==="riser" && <ToolFrame src="/riser-diagram.html" title="Riser Diagram Builder" onClose={()=>setPage("dashboard")}/>}
        {page==="panel-schedule" && <ToolFrame src="/panel-schedule.html" title="Panel Schedule Builder" onClose={()=>setPage("dashboard")}/>}
        {page==="supply" && <ToolFrame src="/supply-locator.html" title="Supply House Locator" onClose={()=>setPage("dashboard")}/>}
        {page==="bids" && <BidBuilder
          bids={bids} setBids={setBids}
          clients={clients} setClients={setClients}
          estimates={estimates} setEstimates={setEstimates}
          invoices={invoices} setInvoices={setInvoices}
          jobs={jobs} setJobs={setJobs} apiSaveJob={apiSaveJob}
          clientName={clientName}
          fmt={fmt} fmtDate={fmtDate}
          markup={markup}
          openInvoiceModal={openInvoiceModal}
          matPrices={matPrices} setMatPrices={setMatPrices} apiSaveMatPrices={apiSaveMatPrices}
          matPriceHistory={matPriceHistory} setMatPriceHistory={setMatPriceHistory}
          apiKey={true}
          apiSaveBid={apiSaveBid}
          apiDeleteBid={apiDeleteBid}
          apiUpdateBid={apiUpdateBid}
          apiSaveEstimate={apiSaveEstimate}
          apiUpdateEstimate={apiUpdateEstimate}
          apiSaveInvoice={apiSaveInvoice}
          navigateTo={navigateTo} setNavigateTo={setNavigateTo}
          companyInfo={companyInfo}
          apiSaveClient={apiSaveClient}
          openClientModal={openClientModal}
          calcTotal={calcTotal}
          aiBidUsage={me?.company}
          updateAiBidUsage={updateAiBidUsage}
          deposits={deposits}
          apiSaveDeposit={apiSaveDeposit}
          apiDeleteDeposit={apiDeleteDeposit}
          me={me}
        />}
        {page==="clients" && <Clients
          clients={clients}
          estimates={estimates}
          invoices={invoices}
          openClientModal={openClientModal}
          deleteClient={apiDeleteClient}
          calcTotal={calcTotal}
        />}
        {page==="invoices" && <Invoices
          invoices={invoices}
          expenses={expenses}
          clients={clients}
          clientName={clientName}
          openInvoiceModal={openInvoiceModal}
          apiUpdateInvoice={apiUpdateInvoice}
          apiDeleteInvoice={apiDeleteInvoice}
          fmt={fmt} fmtDate={fmtDate}
          calcTotal={calcTotal}
          companyInfo={companyInfo}
          payments={payments}
          apiSavePayment={apiSavePayment}
          apiDeletePayment={apiDeletePayment}
          me={me}
        />}
        {page==="payments" && <Payments
          invoices={invoices} clientName={clientName} calcTotal={calcTotal}
          fmtDate={fmtDate} fmt={fmt} apiUpdateInvoice={apiUpdateInvoice} setPage={setPage}
        />}
        {page==="deposits" && <Deposits
          deposits={deposits} clients={clients} invoices={invoices} estimates={estimates}
          clientName={clientName} fmt={fmt} fmtDate={fmtDate}
          apiSaveDeposit={apiSaveDeposit} apiDeleteDeposit={apiDeleteDeposit}
        />}
        {page==="messages" && <Messages me={me}/>}
        {page==="scan-invoice" && <ScanInvoice me={me} clients={clients} invoices={invoices} jobs={jobs} clientName={clientName} openInvoiceModal={openInvoiceModal} markup={markup} apiSaveExpense={apiSaveExpense} matPrices={matPrices} matPriceHistory={matPriceHistory} setMatPriceHistory={setMatPriceHistory} apiSaveMatPrices={apiSaveMatPrices}/>}
        {page==="supply-pricing" && <SupplyHousePage matPrices={matPrices} setMatPrices={setMatPrices} matPriceHistory={matPriceHistory} setMatPriceHistory={setMatPriceHistory} apiSaveMatPrices={apiSaveMatPrices}/>}
        {page==="av-lighting" && companyInfo?.features?.led_tools && <AVLightingHub clients={clients} estimates={estimates} apiSaveEstimate={apiSaveEstimate} openInvoiceModal={openInvoiceModal} setPage={setPage} setNavigateTo={setNavigateTo}/>}
        {page==="settings" && <Settings markup={markup} setMarkup={setMarkup} me={me} companyInfo={companyInfo} setCompanyInfo={setCompanyInfo} matPrices={matPrices} setMatPrices={setMatPrices} matPriceHistory={matPriceHistory} setMatPriceHistory={setMatPriceHistory} apiSaveMatPrices={apiSaveMatPrices}/>}
        {page==="ai-accuracy" && me?.user?.isPlatformAdmin && <AiAccuracy/>}
        {page==="beta-requests" && me?.user?.isPlatformAdmin && <BetaRequests/>}
        {page==="admin-companies" && me?.user?.isPlatformAdmin && <AdminCompanies/>}
        {page==="custom-billing" && me?.user?.isPlatformAdmin && <CustomBilling/>}
      </div>
      {clientModal && <NewClientModal client={editingClient} onSave={async(d)=>{ if(d.id){ await apiUpdateClient(d.id, d); } else { const created = await apiSaveClient(d); if(onClientCreated) onClientCreated(created); } setClientModal(false); setEditingClient(null); setOnClientCreated(null); }} onClose={()=>{setClientModal(false); setEditingClient(null); setOnClientCreated(null);}}/>}
      {invoiceModal && <InvoiceModal data={invoiceModal} clients={clients} jobs={jobs} expenses={expenses} apiSaveClient={apiSaveClient} markup={markup} companyInfo={companyInfo} matPriceHistory={matPriceHistory} services={services} setServices={setServices} onSave={async(d)=>{ if(d.id){ await apiUpdateInvoice(d.id, d); } else { await apiSaveInvoice(d); } setInvoiceModal(null); }} onClose={()=>setInvoiceModal(null)}/>}
      {trialExpired && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:24,overflowY:"auto"}}>
          <div style={{background:D.card,borderRadius:16,padding:"40px 32px",maxWidth:740,width:"100%",textAlign:"center"}}>
            <h2 style={{color:D.text,margin:"0 0 8px",fontSize:22}}>Your free trial has ended</h2>
            <p style={{color:D.muted,marginBottom:32,fontSize:14}}>No data lost. Choose a plan to continue using AmpBooks.</p>
            <div style={{display:"flex",gap:16,justifyContent:"center",flexWrap:"wrap",marginBottom:24}}>
              {[
                {key:"solo",name:"Solo",mo:49.99,yr:499.90,desc:"1-2 users"},
                {key:"crew",name:"Crew",mo:99.99,yr:999.90,desc:"3-6 users"},
                {key:"team",name:"Team",mo:179.99,yr:1799.90,desc:"7-12 users"},
              ].map(p=>(
                <div key={p.key} style={{background:D.bg,border:"1px solid "+D.border,borderRadius:12,padding:"24px 20px",minWidth:180,flex:1}}>
                  <div style={{fontWeight:700,fontSize:18,color:D.text,marginBottom:4}}>{p.name}</div>
                  <div style={{color:D.muted,fontSize:12,marginBottom:16}}>{p.desc}</div>
                  <div style={{color:D.accent,fontSize:22,fontWeight:700,marginBottom:2}}>{"$"+p.mo.toFixed(2)}<span style={{fontSize:12,color:D.muted}}>/mo</span></div>
                  <div style={{color:D.muted,fontSize:11,marginBottom:16}}>{"$"+(p.yr/12).toFixed(2)+"/mo billed annually"}</div>
                  <button style={{...st.btn(),width:"100%",marginBottom:8}} onClick={()=>doCheckout(p.key)}>Subscribe Monthly</button>
                  <button style={{...st.btn(),width:"100%",background:D.accent+"22",color:D.accent,border:"1px solid "+D.accent}} onClick={()=>doCheckout(p.key+"_annual")}>Annual (save 17%)</button>
                </div>
              ))}
            </div>
            <p style={{color:D.muted,fontSize:12}}>Questions? <a href="mailto:support@ampbooks.io" style={{color:D.accent}}>support@ampbooks.io</a></p>
          </div>
        </div>
      )}
      {feedbackModal && <Overlay zIndex={200}><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(440px,95vw)"}}>
        <MHdr title="Beta Feedback" onClose={()=>setFeedbackModal(false)}/>
        {feedbackSent ? (
          <div style={{textAlign:"center",padding:"20px 0",color:D.success,fontSize:14}}>Thanks for your feedback!</div>
        ) : (<>
          <Field label="What's on your mind? Bugs, ideas, anything.">
            <textarea style={{...st.input,minHeight:120,resize:"vertical",fontFamily:"inherit"}} value={feedbackText} onChange={e=>setFeedbackText(e.target.value)} placeholder="Tell us what's working, what's not, or what you'd like to see..."/>
          </Field>
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <button style={st.btn("primary")} disabled={feedbackSending||!feedbackText.trim()} onClick={submitFeedback}>{feedbackSending?"Sending...":"Send Feedback"}</button>
          </div>
        </>)}
      </div></Overlay>}
    </div>
  );
}
